import {
  FontWeight,
  StandardTypography,
  StandardTypographyStyle,
  Typeface,
} from './types'

/**
 * A label is its paragraph style trimmed to the text's own ink — Figma's
 * "cap height to baseline" vertical trim — for single-line labels that
 * center and align by what they paint instead of by their leading.
 *
 * @note Firefox does not support `text-box` yet; there a label keeps its
 *   paragraph leading. A consumer that needs exact optical centering can add
 *   a nudge under `@supports not (text-box-trim: trim-both)` the way
 *   Lapidary's `Pill` does.
 */
const label = <S extends StandardTypographyStyle>(style: S) =>
  ({
    ...style,
    textBox: 'trim-both cap alphabetic',
  }) as const

const paragraphs = {
  H700: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Bold,
    fontSize: 36,
    lineHeight: '120%',
  },
  H500: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Bold,
    fontSize: 30,
    lineHeight: '120%',
  },
  H400: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Bold,
    fontSize: 24,
    lineHeight: '120%',
  },
  H300: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Bold,
    fontSize: 20,
    lineHeight: '120%',
  },
  H100: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Bold,
    fontSize: 16,
    lineHeight: '120%',
  },
  H50: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Bold,
    fontSize: 14,
    lineHeight: '120%',
  },
  H25: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Bold,
    fontSize: 10,
    lineHeight: '120%',
  },
  T700: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Medium,
    fontSize: 16,
    lineHeight: '150%',
  },
  T500: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Medium,
    fontSize: 14,
    lineHeight: '150%',
  },
  T300: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Semibold,
    fontSize: 12,
    lineHeight: '150%',
  },
  T100: {
    fontFamily: Typeface.Default,
    fontWeight: FontWeight.Medium,
    fontSize: 10,
    lineHeight: '140%',
  },
  M500: {
    fontFamily: Typeface.Monospace,
    fontWeight: FontWeight.Bold,
    fontSize: 14,
    lineHeight: '150%',
  },
} as const

/**
 * Standardized typography used in the Gem Design System.
 *
 * Each paragraph style has a label twin (`H50` → `HL50`) for single-line
 * labels; see `label` above for what the twin changes.
 *
 * @see the Typography page of the `Web Components` Figma file.
 */
export const Typography = {
  ...paragraphs,
  HL700: label(paragraphs.H700),
  HL500: label(paragraphs.H500),
  HL400: label(paragraphs.H400),
  HL300: label(paragraphs.H300),
  HL100: label(paragraphs.H100),
  HL50: label(paragraphs.H50),
  HL25: label(paragraphs.H25),
  TL700: label(paragraphs.T700),
  TL500: label(paragraphs.T500),
  TL300: label(paragraphs.T300),
  TL100: label(paragraphs.T100),
  ML500: label(paragraphs.M500),
} as const satisfies StandardTypography
